import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";
import { analytics, PII_MASK_CLASS } from "@/lib/analytics";
import { handleActionError, reportUnexpectedError } from "@/lib/errors";
import { normalizeStockReorderLines, summarize } from "@/lib/reorder-diff";
import {
  buildConfirmSelections,
  clearOption,
  countAmbiguousResolved,
  countExactConfirmed,
  groupConfirmLinesBySeller,
  initialReviewState,
  selectOption,
  setIncluded,
  setQuantity,
  type VoiceReviewState
} from "@/lib/voice-order";
import {
  useVoiceOrderConfirm,
  useVoiceOrderParse
} from "@/queries/voice-order";
import { useStoreCurrencyCode } from "@/queries/regions";
import type {
  StoreVoiceOrderConfirmResponse,
  StoreVoiceOrderParseResponse
} from "@repo/api-sdk";
import { toast } from "@medusajs/ui";
import { useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Loader2, Mic, Search } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { MatchReviewStage } from "./MatchReviewStage";
import { SellerReviewStage } from "./SellerReviewStage";
import { VoiceRecorderPanel } from "./VoiceRecorderPanel";

interface VoiceOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The voice-ordering modal (KAN-143). A thin shell around {@link VoiceOrderFlow}
 * - Radix mounts the flow only while the dialog is open and unmounts it on
 * close, so every session starts from a clean recorder state and closing
 * mid-recording releases the microphone via the flow's unmount cleanup.
 */
export function VoiceOrderDialog({
  open,
  onOpenChange
}: VoiceOrderDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] gap-0 overflow-hidden p-0 sm:max-w-lg">
        <VoiceOrderFlow onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

type Phase = "record" | "parsing" | "parse-error" | "match" | "review" | "done";

type ParseErrorKind = "empty" | "timeout" | "failed" | "thin";

interface ParseErrorState {
  kind: ParseErrorKind;
  /** The raw transcript, kept for the `thin` (heard-but-unmatched) fallback. */
  transcript?: string;
}

interface DoneSummary {
  added: number;
  cartCount: number;
}

const HEADER: Record<Phase, { title: string; description?: string }> = {
  record: {
    title: "Order by voice",
    description: "Say what you need and we'll build your cart."
  },
  parsing: { title: "Order by voice" },
  "parse-error": { title: "Order by voice" },
  match: { title: "Check what we heard" },
  review: { title: "Review your order" },
  done: { title: "Added to your cart" }
};

const PARSE_ERROR_COPY: Record<
  ParseErrorKind,
  { title: string; body: string }
> = {
  empty: {
    title: "No speech detected",
    body: "We didn't catch any words. Check your microphone and try recording again."
  },
  timeout: {
    title: "That took too long",
    body: "We couldn't finish in time - the connection may be slow. Please try again."
  },
  failed: {
    title: "Didn't catch that",
    body: "Something went wrong understanding your order. Please try again."
  },
  thin: {
    title: "We heard you, but couldn't match anything",
    body: "Try again, or search the catalog for what you need."
  }
};

/**
 * The full voice-order state machine, mounted only while the dialog is open.
 * Record → parse → stage-1 match review → stage-2 seller-grouped review →
 * commit → done, with a distinct recoverable screen for each parse failure so
 * a live demo never hits a raw error or an endless spinner.
 */
function VoiceOrderFlow({ onClose }: { onClose: () => void }) {
  const [phase, setPhase] = useState<Phase>("record");
  const [parseResult, setParseResult] =
    useState<StoreVoiceOrderParseResponse | null>(null);
  const [reviewState, setReviewState] = useState<VoiceReviewState>({});
  const [confirmPreview, setConfirmPreview] =
    useState<StoreVoiceOrderConfirmResponse | null>(null);
  const [parseError, setParseError] = useState<ParseErrorState | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [doneSummary, setDoneSummary] = useState<DoneSummary | null>(null);

  const storeCurrency = useStoreCurrencyCode() ?? "AUD";
  const navigate = useNavigate();
  const parseMutation = useVoiceOrderParse();
  const confirmMutation = useVoiceOrderConfirm();

  // Stage 2 is grouped from the confirm dry-run response; recomputed only when
  // that response changes.
  const grouped = useMemo(
    () =>
      confirmPreview
        ? groupConfirmLinesBySeller(
            normalizeStockReorderLines(confirmPreview.lines)
          )
        : null,
    [confirmPreview]
  );

  const goToSearch = useCallback(
    (query: string) => {
      navigate({ to: "/search", search: query ? { q: query } : {} });
      onClose();
    },
    [navigate, onClose]
  );

  const handleParseSuccess = useCallback(
    (result: StoreVoiceOrderParseResponse) => {
      const transcript = result.transcript?.trim() ?? "";
      if (result.items.length === 0) {
        setParseError(
          transcript ? { kind: "thin", transcript } : { kind: "empty" }
        );
        setPhase("parse-error");
        return;
      }
      setParseResult(result);
      setReviewState(initialReviewState(result.items));
      setPhase("match");
    },
    []
  );

  const handleParseError = useCallback((error: unknown) => {
    // A parse that *failed* (a server fault, a timeout) is ours to hear about.
    // Not to be confused with `handleParseSuccess`'s empty and thin outcomes,
    // where the parse worked and simply found nothing: those are expected and
    // never reach here, so they are never reported as exceptions. The screen
    // copy below is fixed, never the server's text.
    reportUnexpectedError(error, "voice order parse");
    const kind: ParseErrorKind =
      error instanceof Error && error.name === "AbortError"
        ? "timeout"
        : "failed";
    setParseError({ kind });
    setPhase("parse-error");
  }, []);

  const handleRecordingComplete = useCallback(
    (blob: Blob) => {
      setConfirmError(null);
      setParseError(null);
      setPhase("parsing");
      parseMutation.mutate(blob, {
        onSuccess: handleParseSuccess,
        onError: handleParseError
      });
    },
    [parseMutation, handleParseSuccess, handleParseError]
  );

  const recorder = useAudioRecorder({ onComplete: handleRecordingComplete });

  const backToRecord = useCallback(() => {
    setParseError(null);
    setConfirmError(null);
    recorder.reset();
    setPhase("record");
  }, [recorder]);

  const handleContinueToReview = useCallback(() => {
    setConfirmError(null);
    const selections = buildConfirmSelections(reviewState);
    if (selections.length === 0) {
      return;
    }
    confirmMutation.mutate(
      { selections, dryRun: true },
      {
        onSuccess: (result) => {
          setConfirmPreview(result);
          setPhase("review");
        },
        onError: (error) =>
          setConfirmError(
            handleActionError(error, {
              boundary: "voice order review",
              fallback: "We couldn't prepare your order. Please try again."
            })
          )
      }
    );
  }, [reviewState, confirmMutation]);

  const handleConfirmAndAdd = useCallback(() => {
    setConfirmError(null);
    const selections = buildConfirmSelections(reviewState);
    if (selections.length === 0) {
      return;
    }
    // Computed pre-collapse (per spoken item, from parseResult + reviewState)
    // rather than from the post-collapse `selections`/confirm response, which
    // are already merged by variant and would lose the per-item counts.
    const items = parseResult?.items ?? [];
    const ambiguousResolved = countAmbiguousResolved(items, reviewState);
    const exactConfirmed = countExactConfirmed(items, reviewState);
    confirmMutation.mutate(
      { selections, dryRun: false },
      {
        onSuccess: (result) => {
          const counts = summarize(normalizeStockReorderLines(result.lines));
          setDoneSummary({
            added: counts.added,
            cartCount: result.cart_ids.length
          });
          setPhase("done");
          if (counts.added > 0) {
            toast.success(
              `Added ${counts.added} ${counts.added === 1 ? "item" : "items"} to your cart`
            );
          } else {
            toast.info("Nothing was available to add right now");
          }
          analytics.voiceOrderConfirmed({
            spoken_item_count: parseResult?.items.length ?? 0,
            added_count: counts.added,
            repriced_count: counts.repriced,
            unavailable_count: counts.unavailable,
            cart_count: result.cart_ids.length,
            ambiguous_resolved: ambiguousResolved,
            exact_confirmed: exactConfirmed
          });
        },
        onError: (error) =>
          setConfirmError(
            handleActionError(error, {
              boundary: "voice order add to cart",
              fallback:
                "We couldn't add these items to your cart. Please try again."
            })
          )
      }
    );
  }, [reviewState, confirmMutation, parseResult]);

  const restart = useCallback(() => {
    setParseResult(null);
    setReviewState({});
    setConfirmPreview(null);
    setParseError(null);
    setConfirmError(null);
    setDoneSummary(null);
    recorder.reset();
    setPhase("record");
  }, [recorder]);

  const header = HEADER[phase];

  return (
    <>
      <DialogHeader className="border-border border-b p-6 pb-4 text-left">
        <DialogTitle className="flex items-center gap-2">
          <Mic
            className="h-5 w-5 text-purple-600 dark:text-purple-400"
            aria-hidden="true"
          />
          {header.title}
        </DialogTitle>
        {header.description ? (
          <DialogDescription>{header.description}</DialogDescription>
        ) : (
          // Radix requires a description node for a labelled dialog; keep an
          // sr-only one on phases whose body carries its own copy.
          <DialogDescription className="sr-only">
            {header.title}
          </DialogDescription>
        )}
      </DialogHeader>

      {phase === "record" ? (
        <VoiceRecorderPanel
          status={recorder.status}
          error={recorder.error}
          remainingMs={recorder.remainingMs}
          onStart={() => void recorder.start()}
          onStop={recorder.stop}
          onSearchFallback={() => goToSearch("")}
        />
      ) : null}

      {phase === "parsing" ? (
        <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
          <Loader2
            className="h-8 w-8 animate-spin text-purple-600 dark:text-purple-400"
            aria-hidden="true"
          />
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            Finding your products…
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            This can take a few seconds.
          </p>
        </div>
      ) : null}

      {phase === "parse-error" && parseError ? (
        <div className="flex flex-col items-center gap-4 px-6 py-8 text-center">
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {PARSE_ERROR_COPY[parseError.kind].title}
            </h3>
            <p className="mx-auto max-w-xs text-sm text-gray-500 dark:text-gray-400">
              {PARSE_ERROR_COPY[parseError.kind].body}
            </p>
          </div>
          {parseError.kind === "thin" && parseError.transcript ? (
            <p
              className={`w-full rounded-md bg-gray-50 px-3 py-2 text-sm italic text-gray-600 dark:bg-neutral-800 dark:text-gray-300 ${PII_MASK_CLASS}`}
            >
              “{parseError.transcript}”
            </p>
          ) : null}
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
            <Button type="button" onClick={backToRecord}>
              <Mic className="mr-2 h-4 w-4" aria-hidden="true" />
              Try again
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => goToSearch(parseError.transcript ?? "")}
            >
              <Search className="mr-2 h-4 w-4" aria-hidden="true" />
              Search the catalog
            </Button>
          </div>
        </div>
      ) : null}

      {phase === "match" && parseResult ? (
        <MatchReviewStage
          transcript={parseResult.transcript}
          items={parseResult.items}
          state={reviewState}
          currencyCode={storeCurrency}
          isResolving={confirmMutation.isPending}
          error={confirmError}
          onSelectOption={(index, variantId) =>
            setReviewState((state) => selectOption(state, index, variantId))
          }
          onClearOption={(index) =>
            setReviewState((state) => clearOption(state, index))
          }
          onQuantityChange={(index, quantity) =>
            setReviewState((state) => setQuantity(state, index, quantity))
          }
          onToggleInclude={(index, included) =>
            setReviewState((state) => setIncluded(state, index, included))
          }
          onSearch={goToSearch}
          onCancel={restart}
          onContinue={handleContinueToReview}
        />
      ) : null}

      {phase === "review" && grouped ? (
        <SellerReviewStage
          grouped={grouped}
          currencyCode={confirmPreview?.currency_code ?? storeCurrency}
          isAdding={confirmMutation.isPending}
          error={confirmError}
          onSearch={goToSearch}
          onBack={() => {
            setConfirmError(null);
            setPhase("match");
          }}
          onConfirm={handleConfirmAndAdd}
        />
      ) : null}

      {phase === "done" && doneSummary ? (
        <div className="flex flex-col items-center gap-4 px-6 py-8 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-50 dark:bg-green-900/20">
            <CheckCircle2
              className="h-8 w-8 text-green-600 dark:text-green-400"
              aria-hidden="true"
            />
          </div>
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {doneSummary.added > 0
                ? `Added ${doneSummary.added} ${doneSummary.added === 1 ? "item" : "items"} to your cart`
                : "Nothing was added"}
            </h3>
            {doneSummary.added > 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {doneSummary.cartCount > 1
                  ? `Split across ${doneSummary.cartCount} supplier carts.`
                  : "Ready in your cart."}
              </p>
            ) : null}
          </div>
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
            <Button type="button" variant="outline" onClick={restart}>
              <Mic className="mr-2 h-4 w-4" aria-hidden="true" />
              New voice order
            </Button>
            <Button
              type="button"
              onClick={() => {
                navigate({ to: "/cart" });
                onClose();
              }}
            >
              View cart
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
